import { Directive, ElementRef, Input, OnChanges, OnDestroy, inject, output } from '@angular/core';
import { PhotosApiService } from '@core/api/services/photos-api.service';

@Directive({ selector: 'img[appAuthenticatedPhoto]', standalone: true })
export class AuthenticatedPhotoDirective implements OnChanges, OnDestroy {
  @Input() appAuthenticatedPhoto?: string | null;
  readonly photoLoadingChange = output<boolean>();
  private readonly photos = inject(PhotosApiService);
  private readonly element = inject(ElementRef<HTMLImageElement>);
  private generation = 0;
  private retained?: string;
  private pending?: { path: string; cancel: () => void };
  private observer?: IntersectionObserver;
  ngOnChanges() {
    const generation = ++this.generation;
    this.observer?.disconnect();
    this.pending?.cancel();
    this.pending = undefined;
    const path = this.appAuthenticatedPhoto;
    const image = this.element.nativeElement;
    if (!path) {
      if (this.retained) this.photos.release(this.retained);
      this.retained = undefined;
      image.removeAttribute('src');
      image.hidden = true;
      this.photoLoadingChange.emit(false);
      return;
    }
    if (path === this.retained) { image.hidden = false; this.photoLoadingChange.emit(false); return; }
    this.photoLoadingChange.emit(true);
    image.hidden = false;
    const load = () => {
      let released = false;
      const request = { path, cancel: () => { if (!released) { released = true; this.photos.release(path); } } };
      this.pending = request;
      this.photos.image(path).then(async url => {
        const preload = new Image();
        preload.src = url;
        await preload.decode();
        if (generation !== this.generation || this.pending !== request) { request.cancel(); return; }
        const previous = this.retained;
        image.src = url;
        image.hidden = false;
        this.retained = path;
        this.pending = undefined;
        released = true;
        if (previous) this.photos.release(previous);
        this.photoLoadingChange.emit(false);
      }).catch(() => {
        request.cancel();
        if (this.pending === request) this.pending = undefined;
        if (generation === this.generation) {
          if (!this.retained) image.hidden = true;
          this.photoLoadingChange.emit(false);
        }
      });
    };
    if (typeof IntersectionObserver === 'undefined') load();
    else {
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { this.observer?.disconnect(); load(); }
      }, { rootMargin: '100px' });
      this.observer.observe(this.element.nativeElement);
    }
  }
  ngOnDestroy() { ++this.generation; this.observer?.disconnect(); this.pending?.cancel(); if (this.retained) this.photos.release(this.retained); }
}
